<script setup>
  import { ref, onMounted, defineExpose, getCurrentInstance } from "vue";
  import { useRouter } from 'vue-router'
  import Modal from "../../../../../components/ui-components/modal/Modal.vue";
  import {usePageLoadingStore} from "../../../../../stores/pageLoading";
  import { useUserStore } from "../../../stores/user";
  import { storeToRefs } from "pinia";
  import JobFairJobNoticeDetailModal from "../../../components/modal/jobFair/JobFairJobNoticeDetailModal.vue";
  import JobFairReusmeCreatePolicyModal from '../../../components/modal/jobFair/JobFairReusmeCreatePolicyModal.vue';
  import KSAPrimaryButton from "@/components/ui-components/button/KSAPrimaryButton.vue";
  import mypageJobFairResumeService from "../../../services/mypage/jobFair/mypageJobFairResumeService";
  import mypageJobFairService from "../../../services/mypage/jobFair/mypageJobFairService";

  const userStore = useUserStore();
  const { user, isAuthenticated } = storeToRefs(userStore);
  const loadingStore = usePageLoadingStore()
  const { $pageListBoardNo, $number, $yyyymmddHHmmss, confirm, alert } = getCurrentInstance().appContext.config.globalProperties;

  const router = useRouter()

  const params = ref({});

  const currentPage = ref(1);
  const pageSize = ref(5);
  const totalPages = ref(1);
  const totalCount = ref(0);
  const list = ref([]);

  const baseModal = ref(null);
  const resolvePromise = ref(null);

  const jobNoticeModal = ref(null);
  const jobFairReusmeCreatePolicyModal = ref(null);

  onMounted(()=>{
    setTimeout(()=>{
      baseModal.value.isAdminWrap = true;
    }, 500);
  })

  // 면접제안 시 채용공고 목록 조회
  const retrieveJobNoticeList = (corpId, resumeId) => {
    const paramObj = {
      corpId: corpId || params.value.corpId,
      resumeId: resumeId || params.value.resumeId,
      pageNumber: currentPage.value,
      size: pageSize.value,
    };

    loadingStore.loading = true;
    mypageJobFairResumeService.retrieveJobNoticeListPagingWithResumeId(paramObj)
      .then( (res) => {
        loadingStore.loading = false;
        if(res.data.code.toLowerCase() === 'success') {
          const result = res.data.result;
          totalPages.value = result.totalPages;
          totalCount.value = result.totalElements;
          list.value = $pageListBoardNo(result.content, totalCount.value, pageSize.value, currentPage.value);
        }
      })
      .catch( (error) =>  {
        console.log(error);
      });
  }

  // 면접제안
  const onClickInterviewProposal = async (jobNoticeSeq) => {
    const ok = await confirm(["선택한 채용공고로", "면접제안을 하시겠습니까?"], "면접제안");
    if(ok) {
      _confirm({jobNoticeSeq});
    }
  }

  // 채용공고 보기
  const onClickJobNotice = async (jobNotice) => {
    const result = await jobNoticeModal.value.show({
      jobNoticeSeq: jobNotice.jobNoticeSeq,
      corpId: jobNotice.corpId,
      mode: 'interview',
      interviewResumeId: jobNotice.resumeId,
      resumeId: params.value.resumeId,
    });

    // 상세 팝업에서 면접제안을 완료했으면 목록 팝업도 닫는다.
    // 여기서는 jobNoticeSeq를 상위로 올리지 않아 중복 API 호출을 막는다.
    if (result && result.interviewProposalYn === 'Y') {
      _confirm({ interviewProposalYn: 'Y' });
    }
  }

  // 새로운 채용공고 등록하기
  const onClickCreateJobNotice = () => {
    const corpId = user.value.userInfo.corporation.corpId;

    // 기업 채용공고 등록 시 기업정보 등록 및 정보제공동의여부 확인
    mypageJobFairService.retrieveRecruitPrivacyAgreeYnByCorpId(corpId)
      .then( async (res) => {
        loadingStore.loading = false;
        if(res.data.code.toLowerCase() === 'success') {
          const result = res.data.result;

          if (result !== 'Y') {  // 기업정보 및 정보제공동의여부 내역 없을 때
            const ok = await jobFairReusmeCreatePolicyModal.value.show();
            if(ok) {
              router.push({ name: 'MyPageCorpInfoCreate', query : { resumeId: params.value.resumeId }});
            }
          } else {
            router.push({ name: 'MyPageCorpJobNoticeCreate', query : { resumeId: params.value.resumeId }});
          }
        }
      })
      .catch( (error) =>  {
        loadingStore.loading = false;
      });
  }

  const show = ({ corpId, resumeId }) => {
    params.value = {
      corpId, resumeId,
    }

    if(corpId) {
      retrieveJobNoticeList(corpId);
    }

    baseModal.value.open();
    return new Promise((resolve, _) => {
      resolvePromise.value = resolve;
    });
  };

  const _confirm = (result) => {
    baseModal.value.close();
    resolvePromise.value(result || true);
  };

  const cancel = (result) => {
    baseModal.value.close();
    resolvePromise.value(result || false);
  };

  const onSelectedPage = (pageNo) => {
    currentPage.value = pageNo;
    retrieveJobNoticeList();
  }

  defineExpose({
    show,
  })
</script>

<template>
  <Modal ref="baseModal">
    <div class="modal-inner xl-size">
      <button class="close" @click="cancel"></button>
      <div class="modal-title">
        <h3>채용공고 선택</h3>
        <p>
          기존 등록된 채용공고를 선택하여 인재에게 면접제안을 할 수 있습니다.<br>
          새로운 채용공고 작성을 원하실 경우 [새로운 채용공고 등록하기]를 눌러주세요.
        </p>
      </div>
      <div class="modal-body">
        <div class="list-top">
          <p>총 <span>{{ $number(totalCount)}}</span>개</p>
        </div>
        <div class="table-form">
          <table class="vertical" v-if="list !== null">
            <colgroup>
              <col width="50px" />
              <col />
              <col width="220px" />
              <col width="220px" />
              <col width="50px" />
            </colgroup>
            <tr>
              <th class="text-center">번호</th>
              <th class="text-center">채용공고 제목</th>
              <th class="text-center">최근 수정일</th>
              <th class="text-center">등록일</th>
              <th class="text-center">선택</th>
            </tr>
            <tr v-for="jobNotice in list">
              <td class="text-center">{{ jobNotice.boardNo }}</td>
              <td class="text-center text-overflow long-overflow">
                <p @click="onClickJobNotice(jobNotice)">
                  <span class="clickable">{{ jobNotice.jobName }}</span>
                </p>
              </td>
              <td class="text-center">{{ $yyyymmddHHmmss(jobNotice.modifyDate) }}</td>
              <td class="text-center">{{ $yyyymmddHHmmss(jobNotice.createDate) }}</td>
              <td class="link text-center">
                <!-- 채용공고 선택 목록 데이터에서 resumeId에 데이터가 있으면 제안완료로 체크 (버튼 활성화/비활성화를 위한 데이터) -->
                <KSAPrimaryButton v-if="jobNotice.resumeId" text="제안완료" disabled="disabled" />
                <KSATextButton text="선택" v-else @click="onClickInterviewProposal(jobNotice.jobNoticeSeq)" />
              </td>
            </tr>
            <tr v-if="list.length === 0">
              <td class="text-center" colspan="5">
                등록된 채용공고가 없습니다.
              </td>
            </tr>
            <tr>
              <th class="text-left" style="font-weight: 400;"></th>
              <th colspan="4" class="link text-right" style="font-weight: 400; ">
                <KSATextButton text="[새로운 채용공고 등록하기]" @click="onClickCreateJobNotice()" />
              </th>
            </tr>
          </table>
        </div>
        <Paginate
            v-if="list.length > 0"
            v-model="currentPage"
            :page-count="totalPages"
            :click-handler="onSelectedPage"
        />
      </div>
      <div class="modal-footer mt-xs" style="justify-content: center;">
        <KSASecondaryButton class="gray_button" text="취소" @click="cancel()" />
      </div>
    </div>
  </Modal>
  <JobFairJobNoticeDetailModal ref="jobNoticeModal"/> <!-- 채용공고 상세 -->
  <jobFairReusmeCreatePolicyModal ref="jobFairReusmeCreatePolicyModal" />
</template>

<style lang="scss" scoped>
@import "../../../../../assets/scss/theme";

.modal-inner {
  width: 598px !important;
}

.modal-title p span {
  color: $red_color;
  font-weight: 700;
}

.list-top {
  p {
    margin-right: 0;
    padding-left: 2px;
    &:first-of-type {
      padding-left: 0;
    }
  }
}

.table-form {
  p {
    margin-bottom: 0 !important;
  }
  .clickable {
    text-decoration: underline;
    cursor: pointer;
    color: $blue_color !important;
  }
}

</style>
